import { useState } from "react";
import { Lock, Stack, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, STATUS_LABELS, STATUS_ORDER } from "@/lib/api";
import { useEscClose } from "@/lib/useEscClose";

export default function BulkMoveModal({ onClose, onDone, initialIds }) {
  const idsMode = Array.isArray(initialIds) && initialIds.length > 0;
  useEscClose(onClose);
  const [fromNum, setFromNum] = useState("");
  const [toNum, setToNum] = useState("");
  const [toStatus, setToStatus] = useState(STATUS_ORDER[1] || "separacao");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    let body;
    if (idsMode) {
      if (initialIds.length > 500) { setError(`Máximo de 500 pedidos por lote (selecionados: ${initialIds.length}). Reduza a seleção.`); return; }
      body = {
        order_ids: initialIds,
        to_status: toStatus,
        collaborator_code: code,
        collaborator_password: password,
      };
    } else {
      if (!fromNum || !toNum) { setError("Informe o intervalo de pedidos."); return; }
      if (!/^\d+$/.test(fromNum) || !/^\d+$/.test(toNum)) { setError("Números devem ser numéricos."); return; }
      const a = parseInt(fromNum, 10), b = parseInt(toNum, 10);
      const lo = Math.min(a, b), hi = Math.max(a, b);
      if (hi - lo > 500) { setError("Intervalo máximo: 500 pedidos."); return; }
      body = {
        from_number: String(lo),
        to_number: String(hi),
        to_status: toStatus,
        collaborator_code: code,
        collaborator_password: password,
      };
    }
    if (!code || !password) { setError("Informe código e senha do colaborador."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/orders/bulk-move", body);
      setResult(data);
      toast.success(`${data.moved_count} pedido(s) movido(s) para ${STATUS_LABELS[toStatus]}.`);
      onDone?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Falha na operação em lote.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="glass w-full max-w-lg p-8" onClick={(e) => e.stopPropagation()} data-testid="bulk-move-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Stack size={18} weight="bold" />
            <div className="overline">MOVIMENTAÇÃO EM LOTE (ADMIN)</div>
          </div>
          <button onClick={onClose} className="hover:text-red-500" data-testid="bulk-move-close"><X size={20} /></button>
        </div>
        <h2 className="font-head text-2xl font-black mb-2">Mover pedidos em lote</h2>
        <p className="text-sm text-zinc-600 mb-6">
          {idsMode
            ? `${initialIds.length} pedido(s) selecionado(s). Escolha a coluna destino e autentique com código + senha do colaborador.`
            : "Informe o intervalo de números de pedido, escolha a coluna destino e autentique com código + senha do colaborador."}
        </p>

        {result ? (
          <div className="space-y-3">
            <div className="border border-black bg-green-50 px-4 py-3">
              <div className="font-bold">{result.moved_count} pedido(s) movido(s)</div>
              {result.moved?.length > 0 && (
                <div className="text-xs font-mono mt-1 break-all">#{result.moved.join(", #")}</div>
              )}
            </div>
            {result.skipped?.length > 0 && (
              <div className="border border-zinc-300 bg-zinc-50 px-4 py-3 text-sm">
                <div className="font-bold mb-1">Ignorados ({result.skipped.length})</div>
                <div className="text-xs">
                  {result.skipped.slice(0, 20).map((s) => `#${s.bling_number} (${s.reason})`).join(", ")}
                  {result.skipped.length > 20 && ` +${result.skipped.length - 20}`}
                </div>
              </div>
            )}
            {result.not_found?.length > 0 && (
              <div className="border border-[#E60000] bg-red-50 px-4 py-3 text-sm">
                <div className="font-bold mb-1 text-[#E60000]">Não encontrados ({result.not_found.length})</div>
                <div className="text-xs font-mono">#{result.not_found.slice(0, 30).join(", #")}{result.not_found.length > 30 && ` +${result.not_found.length - 30}`}</div>
              </div>
            )}
            <button className="btn w-full mt-2" onClick={onClose} data-testid="bulk-move-ok">Fechar</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {!idsMode && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">De (nº pedido)</label>
                  <input className="input font-mono" inputMode="numeric" required value={fromNum} onChange={(e) => setFromNum(e.target.value.replace(/\D/g, ""))} data-testid="bulk-from" placeholder="994" autoFocus />
                </div>
                <div>
                  <label className="label">Até (nº pedido)</label>
                  <input className="input font-mono" inputMode="numeric" required value={toNum} onChange={(e) => setToNum(e.target.value.replace(/\D/g, ""))} data-testid="bulk-to" placeholder="1000" />
                </div>
              </div>
            )}
            <div>
              <label className="label">Mover para a coluna</label>
              <select className="input" value={toStatus} onChange={(e) => setToStatus(e.target.value)} data-testid="bulk-status">
                {STATUS_ORDER.map((s) => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
            <div className="border-t border-zinc-200 pt-4 space-y-4">
              <div className="overline text-zinc-500 flex items-center gap-2"><Lock size={12} weight="bold" /> AUTENTICAÇÃO</div>
              <div>
                <label className="label">Código do colaborador</label>
                <input className="input font-mono" required value={code} onChange={(e) => setCode(e.target.value)} data-testid="bulk-code" inputMode="numeric" />
              </div>
              <div>
                <label className="label">Senha</label>
                <input type="password" className="input" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="bulk-password" />
              </div>
            </div>
            {error && <div className="border border-[#E60000] bg-red-50 text-[#E60000] px-3 py-2 text-sm" data-testid="bulk-move-error">{error}</div>}
            <div className="flex gap-3 pt-2">
              <button type="button" className="btn btn-ghost flex-1" onClick={onClose}>Cancelar</button>
              <button type="submit" disabled={loading} className="btn flex-1" data-testid="bulk-move-submit">
                {loading ? "Movendo..." : "Mover em lote"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
